import React, { useEffect, useMemo, useState } from 'react';
import {
  FaCloud,
  FaCompass,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaMoon,
  FaThermometerHalf,
  FaTint,
  FaWind,
} from 'react-icons/fa';
import { WiDaySunny, WiDayCloudy, WiCloudy, WiRain } from 'react-icons/wi';
import { YDialog } from '../../../ui/dialog/YDialog';
import { YButton } from '../../../ui/YButton';
import api from '../../../utils/api';
import ForecastScoreRing from './ForecastScoreRing';
import {
  buildDetailFactorBullets,
  dayHasWeather,
  formatForecastTime,
  formatMajorWindow,
  formatPressureInHg,
  formatShortDayLabel,
  getForecastScoreLabel,
  isWindOptimal,
  pickEntityType,
  resolveTodayDay,
  sortOptimalDaysChronologically,
  toForecastCardinal,
} from '../huntForecastUtils';

const OPTIMAL_DAYS_RETRY_DELAY_MS = 1500;

function weatherIconFor(type) {
  switch (type) {
    case 'rainy':
      return WiRain;
    case 'cloudy':
      return WiCloudy;
    case 'partly_cloudy':
      return WiDayCloudy;
    case 'sun':
    default:
      return WiDaySunny;
  }
}

function formatWeatherTypeLabel(type) {
  if (!type || typeof type !== 'string') return null;
  return type.replace(/_/g, ' ');
}

function ScoreDayChip({ day, isToday }) {
  const score = day?.score;
  const { tone } = getForecastScoreLabel(score);
  return (
    <div className={`hf-dialog__score-chip${isToday ? ' is-today' : ''}`}>
      <span className="hf-dialog__score-chip-date">{formatShortDayLabel(day?.date)}</span>
      <span className={`hf-dialog__score-chip-value hf-score-tone--${tone}`}>
        {score != null ? score : '—'}
      </span>
    </div>
  );
}

export default function ForecastStandDetailDialog({
  show,
  onHide,
  pick,
  onTagOut,
  onOpenMap,
}) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [weatherMeta, setWeatherMeta] = useState({ ok: true, error: null, retried: false });

  useEffect(() => {
    if (!show || !pick?.id) {
      setDays([]);
      setError('');
      setWeatherMeta({ ok: true, error: null, retried: false });
      return undefined;
    }
    let cancelled = false;
    const entityType = pickEntityType(pick.kind);
    const basePath = entityType === 'geo-feature' ? '/features' : '/hunt-locations';

    const loadOptimalDays = async (isRetry = false) => {
      setLoading(true);
      setError('');
      try {
        const resp = await api.get(`${basePath}/${pick.id}/optimal_days/?days=8`);
        if (cancelled) return;
        const nextDays = Array.isArray(resp?.data?.days) ? resp.data.days : [];
        const weatherOk = resp?.data?.weather_ok;
        const weatherError = resp?.data?.weather_error || null;
        const hasUsableWeather = weatherOk !== false
          && nextDays.some((day) => dayHasWeather(day?.weather));

        if (!isRetry && !hasUsableWeather) {
          setWeatherMeta({ ok: false, error: weatherError, retried: true });
          await new Promise((resolve) => { setTimeout(resolve, OPTIMAL_DAYS_RETRY_DELAY_MS); });
          if (cancelled) return;
          await loadOptimalDays(true);
          return;
        }

        setDays(nextDays);
        setWeatherMeta({
          ok: hasUsableWeather,
          error: hasUsableWeather ? null : weatherError,
          retried: isRetry,
        });
        setLoading(false);
      } catch {
        if (cancelled) return;
        setError('Unable to load forecast details.');
        setDays([]);
        setWeatherMeta({ ok: false, error: 'request_failed', retried: isRetry });
        setLoading(false);
      }
    };

    loadOptimalDays(false).catch(() => {});
    return () => { cancelled = true; };
  }, [show, pick?.id, pick?.kind]);

  const todayDay = useMemo(() => resolveTodayDay(pick, days), [pick, days]);
  const orderedDays = useMemo(() => sortOptimalDaysChronologically(days), [days]);
  const prevDay = useMemo(() => {
    if (!todayDay?.date) return null;
    const idx = orderedDays.findIndex((d) => d.date === todayDay.date);
    return idx > 0 ? orderedDays[idx - 1] : null;
  }, [orderedDays, todayDay]);
  const factors = useMemo(
    () => buildDetailFactorBullets(todayDay, { prevDay }),
    [todayDay, prevDay],
  );
  const windOptimal = useMemo(() => isWindOptimal({ ...pick, day: todayDay }), [pick, todayDay]);
  const weather = todayDay?.weather || {};
  const WeatherIcon = weatherIconFor(weather.weather_type);
  const windCardinal = weather.wind_cardinal || toForecastCardinal(weather.wind_deg);
  const windSpeed = typeof weather.wind_speed === 'number' ? Math.round(weather.wind_speed) : null;
  const temp = weather.temp_max != null ? Math.round(weather.temp_max) : null;
  const pressure = formatPressureInHg(weather.pressure);
  const humidity = typeof weather.humidity === 'number' ? Math.round(weather.humidity) : null;
  const weatherTypeLabel = formatWeatherTypeLabel(weather.weather_type);
  const TempIcon = weather.weather_type ? WeatherIcon : FaThermometerHalf;
  const todayConditions = [
    {
      key: 'temp',
      Icon: TempIcon,
      useWeatherIcon: Boolean(weather.weather_type),
      label: 'Temp',
      value: temp != null ? `${temp}°F` : '—',
    },
    {
      key: 'wind',
      Icon: FaWind,
      label: 'Wind',
      value: windSpeed != null
        ? `${windCardinal || ''} ${windSpeed} mph`.trim()
        : '—',
    },
    humidity != null
      ? {
          key: 'humidity',
          Icon: FaTint,
          label: 'Humidity',
          value: `${humidity}%`,
        }
      : {
          key: 'conditions',
          Icon: FaCloud,
          label: 'Conditions',
          value: weatherTypeLabel || '—',
          valueClassName: 'text-capitalize',
        },
    {
      key: 'pressure',
      Icon: FaCompass,
      label: 'Pressure',
      value: pressure || '—',
    },
  ];
  const score = todayDay?.score;
  const { label: scoreLabel } = getForecastScoreLabel(score);
  const tz = pick?.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const majors = todayDay?.solunar?.majors || [];
  const minors = todayDay?.solunar?.minors || [];

  const footer = pick ? (
    <>
      {pick.kind === 'club' ? (
        <YButton variant="primary" onClick={() => onTagOut?.(pick)}>
          Tag Out to Hunt
        </YButton>
      ) : (
        <YButton variant="primary" onClick={() => onOpenMap?.(pick)}>
          Open on Map
        </YButton>
      )}
      <YButton variant="secondary" onClick={onHide}>
        Close
      </YButton>
    </>
  ) : null;

  return (
    <YDialog
      show={show}
      onHide={onHide}
      title={pick?.name || 'Stand forecast'}
      size="lg"
      testId="hf-detail-dialog"
      bodyClassName="hf-dialog"
      footer={footer}
      error={error || undefined}
    >
      {!pick ? null : (
        <>
          <div className="hf-dialog__intro">
            <div className="hf-dialog__intro-score">
              <ForecastScoreRing score={score} size={72} />
            </div>
            <div className="hf-dialog__intro-copy">
              <p className="hf-dialog__score-meta">
                <span className={`hf-score-tone--${getForecastScoreLabel(score).tone}`}>
                  {scoreLabel}
                </span>
                {score != null ? ` · ${score}/100` : null}
              </p>
              {pick.club?.name ? (
                <p className="hf-dialog__club">
                  <FaMapMarkerAlt aria-hidden="true" />
                  {pick.club.name}
                </p>
              ) : null}
              <p className="hf-dialog__window-label">
                <FaMoon aria-hidden="true" />
                Next major window
              </p>
              <p className="hf-dialog__window-value">{formatMajorWindow({ ...pick, day: todayDay })}</p>
            </div>
          </div>

          <div className="hf-dialog__section">
            <h3 className="hf-dialog__section-title">Today</h3>
            {!weatherMeta.ok && (
              <p className="hf-dialog__weather-note text-muted small" data-testid="hf-weather-degraded">
                Weather data is temporarily unavailable — scores may be incomplete.
                {weatherMeta.retried ? ' Retried automatically.' : ''}
                {process.env.NODE_ENV === 'development' && weatherMeta.error
                  ? ` (${weatherMeta.error})`
                  : ''}
              </p>
            )}
            {loading && !todayDay ? (
              <p className="hf-dialog__loading">Loading conditions…</p>
            ) : (
              <div className="hf-dialog__conditions">
                {todayConditions.map((item) => (
                  <div key={item.key} className="hf-dialog__condition">
                    {item.useWeatherIcon ? (
                      <item.Icon size={18} aria-hidden="true" />
                    ) : (
                      <item.Icon aria-hidden="true" />
                    )}
                    <div>
                      <p className="hf-dialog__condition-label">{item.label}</p>
                      <p className={`hf-dialog__condition-value${item.valueClassName ? ` ${item.valueClassName}` : ''}`}>
                        {item.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(majors.length > 0 || minors.length > 0) && (
              <div className="hf-dialog__solunar">
                {majors.length > 0 && (
                  <div>
                    <p className="hf-dialog__solunar-label">Majors</p>
                    {majors.map((m, idx) => (
                      <p key={`maj-${idx}`} className="hf-dialog__solunar-value">
                        {formatForecastTime(m.start, tz)}
                        {' – '}
                        {formatForecastTime(m.end, tz)}
                      </p>
                    ))}
                  </div>
                )}
                {minors.length > 0 && (
                  <div>
                    <p className="hf-dialog__solunar-label">Minors</p>
                    {minors.map((m, idx) => (
                      <p key={`min-${idx}`} className="hf-dialog__solunar-value">
                        {formatForecastTime(m.start, tz)}
                        {' – '}
                        {formatForecastTime(m.end, tz)}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {factors.length > 0 && (
              <div className="hf-dialog__factors">
                <p className="hf-dialog__factors-title">
                  <FaInfoCircle aria-hidden="true" />
                  Score factors
                </p>
                {factors.map((f, idx) => (
                  <div key={idx} className="hf-dialog__factor">
                    <span className={`hf-dialog__factor-sign${f.positive ? ' is-positive' : ' is-negative'}`}>
                      {f.positive ? '+' : '−'}
                    </span>
                    <span>{f.label}</span>
                  </div>
                ))}
              </div>
            )}

            <div className={`hf-dialog__wind-note${windOptimal ? ' is-optimal' : ' is-caution'}`}>
              <FaCompass aria-hidden="true" />
              <span>
                {windOptimal
                  ? `Wind from ${windCardinal || '—'} favors this stand's setup.`
                  : `Wind from ${windCardinal || '—'} may carry scent toward deer. Hunt with caution.`}
              </span>
            </div>
          </div>

          <div className="hf-dialog__section">
            <h3 className="hf-dialog__section-title">8-day scores</h3>
            {loading && days.length === 0 ? (
              <p className="hf-dialog__loading">Loading outlook…</p>
            ) : orderedDays.length === 0 ? (
              <p className="hf-dialog__loading">No outlook data available.</p>
            ) : (
              <div className="hf-dialog__score-strip">
                {orderedDays.map((d) => (
                  <ScoreDayChip
                    key={d.date}
                    day={d}
                    isToday={d.date === todayDay?.date}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </YDialog>
  );
}
